import React, { useState, ReactNode } from 'react';
import { GripVertical } from 'lucide-react';

interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

interface TabCardProps {
  title: string;
  tabs: TabItem[];
  additionalHeaderContent?: ReactNode;
  className?: string;
  defaultActiveTab?: string;
}

const TabCard: React.FC<TabCardProps> = ({
  title,
  tabs,
  additionalHeaderContent,
  className = '',
  defaultActiveTab,
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab || tabs[0]?.id || '');

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  return (
    <div
      className={`h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}
      data-card-container
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 relative">
          {/* Drag Handle */}
          <div className="drag-handle absolute top-2 right-2 p-1 rounded opacity-40 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10">
            <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
          </div>

          <div className="flex items-center justify-between p-3 pb-0 pr-12 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h2>
              {additionalHeaderContent}
            </div>
            <div></div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 px-3 py-2 text-sm font-medium transition-colors relative
                  ${
                    activeTab === tab.id
                      ? 'text-pink-600 dark:text-pink-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }
                `}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-500"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">{activeTabContent}</div>
      </div>
    </div>
  );
};

export default TabCard;
